<template>
  <transition name="pay-plans-filter-modal" appear>
    <div
      v-if="value"
      class="pay-plans-filter-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pay-plans-filter-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="pay-plans-filter-modal__card">
        <header class="pay-plans-filter-modal__header">
          <h2 id="pay-plans-filter-modal-title" class="pay-plans-filter-modal__title">
            Filter Fair Pay
          </h2>
          <button
            type="button"
            class="pay-plans-filter-modal__close"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </header>

        <div class="pay-plans-filter-modal__groups">
          <section class="pay-plans-filter-modal__group">
            <span class="pay-plans-filter-modal__group-title">Fair Pay Status</span>
            <RadioInput
              label="Fair Pay Status"
              :availableItems="fairPayStatusRadioItems"
              :checkedItem="fairPayStatusLabel"
              @changeradio="onFairPayStatusChange"
            />
          </section>
          <section class="pay-plans-filter-modal__group">
            <span class="pay-plans-filter-modal__group-title">Pay Plans</span>
            <RadioInput
              label="Pay Plans"
              :availableItems="payPlanRadioItems"
              :checkedItem="payPlanLabel"
              @changeradio="onPayPlanChange"
            />
          </section>
          <section class="pay-plans-filter-modal__group">
            <span class="pay-plans-filter-modal__group-title">Changes</span>
            <RadioInput
              label="Changes"
              :availableItems="changeRadioItems"
              :checkedItem="changeLabel"
              @changeradio="onChangeFilterChange"
            />
          </section>
        </div>

        <div class="pay-plans-filter-modal__divider" />

        <section class="pay-plans-filter-modal__department">
          <span class="pay-plans-filter-modal__section-title">Department</span>
          <DepartmentPickerField
            browse-list
            browse-list-requires-query
            :value="department"
            :departments="departments"
            :loading="loading"
            @input="setDepartment"
            @search="handleSearch"
            @clear="handleClear"
          />

          <p v-if="!department" class="pay-plans-filter-modal__hint">
            Leave empty to show employees across the whole organization.
          </p>

          <template v-else>
            <div
              class="pay-plans-filter-modal__scope"
              role="radiogroup"
              aria-label="Department scope"
            >
              <button
                type="button"
                class="pay-plans-filter-modal__scope-option"
                :class="{ 'pay-plans-filter-modal__scope-option--active': scope === 'direct' }"
                role="radio"
                :aria-checked="scope === 'direct'"
                @click="scope = 'direct'"
              >
                This department only
              </button>
              <span class="pay-plans-filter-modal__scope-divider" aria-hidden="true" />
              <button
                type="button"
                class="pay-plans-filter-modal__scope-option"
                :class="{ 'pay-plans-filter-modal__scope-option--active': scope === 'subtree' }"
                role="radio"
                :aria-checked="scope === 'subtree'"
                @click="scope = 'subtree'"
              >
                Include all sub-departments
              </button>
            </div>
            <p class="pay-plans-filter-modal__hint pay-plans-filter-modal__hint--selected">
              {{ scopeHint }}
            </p>
          </template>
        </section>

        <div class="pay-plans-filter-modal__divider" />

        <footer class="pay-plans-filter-modal__footer">
          <button type="button" class="pay-plans-filter-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </footer>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import DepartmentPickerField from "@components/DepartmentPickerField/DepartmentPickerField.vue";
import Icon from "@components/icons/Icon.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const FAIR_PAY_STATUS_OPTIONS = [
  { label: "No filter", value: null },
  { label: "Fair", value: "fair" },
  { label: "Unfair", value: "unfair" },
];

const PAY_PLAN_OPTIONS = [
  { label: "No filter", value: null },
  { label: "Has Current", value: "has_current" },
  { label: "Does Not Have Current", value: "does_not_have_current" },
];

const CHANGE_OPTIONS = [
  { label: "No filter", value: null },
  { label: "No Pending Changes", value: "no_pending_changes" },
  { label: "Has Evaluation", value: "has_evaluation" },
  { label: "Has Proposal", value: "has_proposal" },
  { label: "Has Rejected Proposal", value: "has_rejected_proposal" },
  { label: "Has Upcoming", value: "has_upcoming" },
  { label: "Not Eligible", value: "not_eligible" },
  { label: "Eligible", value: "eligible" },
];

const props = defineProps({
  value: { type: Boolean, default: false },
  initialFairPayStatus: {
    type: String,
    default: null,
    validator: (value) => value === null || ["fair", "unfair"].includes(value),
  },
  initialPayPlan: {
    type: String,
    default: null,
    validator: (value) =>
      value === null || ["has_current", "does_not_have_current"].includes(value),
  },
  initialChange: {
    type: String,
    default: null,
    validator: (value) =>
      value === null ||
      [
        "no_pending_changes",
        "has_evaluation",
        "has_proposal",
        "has_rejected_proposal",
        "has_upcoming",
        "not_eligible",
        "eligible",
      ].includes(value),
  },
  initialDepartment: { type: Object, default: null },
  initialScope: {
    type: String,
    default: "subtree",
    validator: (value) => ["direct", "subtree"].includes(value),
  },
  departments: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(["input", "close", "apply", "reset", "search", "clear"]);

const fairPayStatusOptions = FAIR_PAY_STATUS_OPTIONS;
const payPlanOptions = PAY_PLAN_OPTIONS;
const changeOptions = CHANGE_OPTIONS;
const fairPayStatusRadioItems = fairPayStatusOptions.map(({ label }) => ({ label }));
const payPlanRadioItems = payPlanOptions.map(({ label }) => ({ label }));
const changeRadioItems = changeOptions.map(({ label }) => ({ label }));

const fairPayStatus = ref(props.initialFairPayStatus);
const payPlan = ref(props.initialPayPlan);
const change = ref(props.initialChange);
const department = ref(props.initialDepartment);
const scope = ref(props.initialScope);

const fairPayStatusLabel = computed(() =>
  labelForValue(fairPayStatusOptions, fairPayStatus.value)
);
const payPlanLabel = computed(() => labelForValue(payPlanOptions, payPlan.value));
const changeLabel = computed(() => labelForValue(changeOptions, change.value));

const scopeHint = computed(() =>
  scope.value === "subtree"
    ? "Showing this department plus every department nested beneath it, at any depth."
    : "Showing employees belonging directly to this department only."
);

watch(
  () => props.value,
  (open) => {
    if (!open) return;
    syncFromProps();
    emit("search", "");
  },
  { immediate: true }
);

function syncFromProps() {
  fairPayStatus.value = props.initialFairPayStatus;
  payPlan.value = props.initialPayPlan;
  change.value = props.initialChange;
  department.value = props.initialDepartment;
  scope.value = props.initialScope;
}

function labelForValue(options, value) {
  return options.find((option) => option.value === value)?.label || "No filter";
}

function valueForLabel(options, label) {
  return options.find((option) => option.label === label)?.value ?? null;
}

function onFairPayStatusChange(payload) {
  fairPayStatus.value = valueForLabel(fairPayStatusOptions, payload?.value);
}

function onPayPlanChange(payload) {
  payPlan.value = valueForLabel(payPlanOptions, payload?.value);
}

function onChangeFilterChange(payload) {
  change.value = valueForLabel(changeOptions, payload?.value);
}

function setDepartment(value) {
  department.value = value;
  if (!value) scope.value = "subtree";
}

function handleSearch(query) {
  emit("search", query);
}

function handleClear() {
  emit("clear");
  emit("search", "");
}

function handleReset() {
  fairPayStatus.value = null;
  payPlan.value = null;
  change.value = null;
  department.value = null;
  scope.value = "subtree";
  emit("reset");
  emit("search", "");
}

function handleApply() {
  emit("apply", {
    fairPayStatus: fairPayStatus.value,
    payPlan: payPlan.value,
    change: change.value,
    department: department.value,
    scope: department.value ? scope.value : null,
  });
  emit("input", false);
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => document.addEventListener("keydown", handleKeydown));
onUnmounted(() => document.removeEventListener("keydown", handleKeydown));
</script>

<style scoped>
.pay-plans-filter-modal-enter-active,
.pay-plans-filter-modal-leave-active {
  transition: opacity 180ms ease;
}

.pay-plans-filter-modal-enter-from,
.pay-plans-filter-modal-leave-to {
  opacity: 0;
}

.pay-plans-filter-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.pay-plans-filter-modal__card {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 816px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #fff;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.pay-plans-filter-modal__header,
.pay-plans-filter-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.pay-plans-filter-modal__title {
  margin: 0;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.pay-plans-filter-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: -4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.pay-plans-filter-modal__close:hover {
  background: rgba(152, 162, 179, 0.12);
}

.pay-plans-filter-modal__close:focus-visible,
.pay-plans-filter-modal__reset:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.pay-plans-filter-modal__groups {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;
  align-items: start;
}

.pay-plans-filter-modal__group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.pay-plans-filter-modal__group-title,
.pay-plans-filter-modal__section-title {
  color: #667085;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
}

.pay-plans-filter-modal__divider {
  flex: 0 0 auto;
  width: 100%;
  height: 1px;
  background: #eaecf0;
}

.pay-plans-filter-modal__department {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pay-plans-filter-modal__hint {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.pay-plans-filter-modal__hint--selected {
  text-align: right;
}

.pay-plans-filter-modal__scope {
  display: flex;
  width: 100%;
  overflow: hidden;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
}

.pay-plans-filter-modal__scope-option {
  flex: 1 1 0;
  min-width: 0;
  padding: 10px 16px;
  border: 0;
  background: #fff;
  color: #344054;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
}

.pay-plans-filter-modal__scope-option--active {
  background: #f9f5ff;
  color: #6941c6;
}

.pay-plans-filter-modal__scope-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.pay-plans-filter-modal__scope-divider {
  width: 1px;
  background: #d0d5dd;
}

.pay-plans-filter-modal__reset {
  padding: 4px;
  margin: -4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.pay-plans-filter-modal__reset:hover {
  background: #f9fafb;
  color: #344054;
}

.pay-plans-filter-modal__group :deep(.filter-items) {
  display: none;
}

.pay-plans-filter-modal__group :deep(> div > .pl-2) {
  padding-left: 0 !important;
}

.pay-plans-filter-modal__group :deep(.v-input--radio-group),
.pay-plans-filter-modal__group :deep(.v-input--selection-controls) {
  margin: 0;
  padding: 0;
}

.pay-plans-filter-modal__group :deep(.v-input__slot) {
  margin: 0;
}

.pay-plans-filter-modal__group :deep(.v-input--radio-group__input) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pay-plans-filter-modal__group :deep(.v-radio) {
  min-height: 20px;
  margin: 0;
  padding: 0;
}

.pay-plans-filter-modal__group :deep(.v-input--selection-controls__input) {
  width: 16px;
  height: 16px;
  margin: 0 8px 0 0;
}

.pay-plans-filter-modal__group :deep(.v-input--selection-controls__ripple) {
  display: none;
}

.pay-plans-filter-modal__group :deep(.v-icon) {
  width: 16px;
  height: 16px;
  font-size: 16px;
}

.pay-plans-filter-modal__group :deep(.v-label) {
  color: #344054;
  font-size: 13px;
  line-height: 20px;
}

.pay-plans-filter-modal__group :deep(.mdi-radiobox-marked::before) {
  content: url("../assets/checkedRadioIconRing.svg") !important;
}

@media (max-width: 720px) {
  .pay-plans-filter-modal__groups {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pay-plans-filter-modal-enter-active,
  .pay-plans-filter-modal-leave-active {
    transition: none;
  }
}
</style>
